import { SvelteSet } from 'svelte/reactivity'
import { router } from '@inertiajs/svelte'

export function useSelection({ initialItems = [], allIds = [] } = {}) {
  
  const url = new URL(window.location.href)
  const idsParam = url?.searchParams?.get('ids')
  const stringIds = idsParam && idsParam !== '*' ? idsParam.split('+') : []
  const initialIds = idsParam === '*'
    ? allIds
    : stringIds.map(id => parseInt(id, 10)).filter(Number.isFinite)
  
  const initialSelected = $state(initialIds)
  
	let selected = $state(new SvelteSet(initialSelected))
	let items = $state(initialItems)
  
  let selectedItems = $derived(items.filter(item => selected.has(item.id)))
  let ids = $derived(Array.from(selected))

  let totalCount = $derived(allIds?.length || items?.length || 0)

  let pageSelectedCount = $derived(items.reduce((count, item) => count + (selected.has(item.id) ? 1 : 0), 0))

  let pageSelected = $derived(items.length > 0 && pageSelectedCount === items.length)
  let allSelected = $derived(allIds.length > 0 && allIds.every(id => selected.has(id)))
  let pageOnlySelected = $derived(items.length > 0 && pageSelected && selected.size === items.length)

	function has(id) {
		return selected.has(id)
	}

  function updateUrl() {
    const url = new URL(window.location.href)

    if (ids.length === 0) {
      url.searchParams.delete('ids')
    } else if (allSelected) {
      url.searchParams.set('ids', '*')
    } else {
      url.searchParams.set('ids', ids.join("+"))
    }
    history.pushState(null, "", url.toString())
  }

	function toggle(id, event) {
		const currentIndex = items.findIndex(item => item.id === id)
    selected.has(id) ? selected.delete(id) : selected.add(id)
		
    if (event?.shiftKey) {

      const selectionIndexes = ids.map(item => ids.findIndex(i => i === item))
      const previousIndex = Math.max(...selectionIndexes)

      // Fill in any items between the current index and the previous index
      for (let i = Math.min(currentIndex, previousIndex); i <= Math.max(currentIndex, previousIndex); i++) {
        selected.add(ids[i])
      }
  
    }

    updateUrl()

	}

	function selectAll() {
		// Add all current page items to selection, preserving other selections
		items.forEach(item => selected.add(item.id))
    updateUrl()
	}

	function selectNone() {
		// Only remove current page items from selection
		items.forEach(item => selected.delete(item.id))
    updateUrl()
	}

  function selectPage() {
    clear()
    selectAll()
  }

	function selectCollection() {
    clear()
		allIds.forEach(id => selected.add(id))
    updateUrl()
	}

	function selectAllNone() {
		if (pageSelected) {
			selectNone()
		} else {
			selectAll()
		} 
	}

  function setItems(newItems) {
    items = newItems
  }

  function mergeItems(newItems) {
    items = [...items, ...newItems]
  }

	function clear() {
		selected.clear()
    updateUrl()
	}

	return {
		selected,
		// items,
		has,
		toggle,
		selectAll,
		selectNone,
		selectAllNone,
		selectPage,
		selectCollection,
		clear,
    setItems,
    mergeItems,
    get items() {
      return items
    },
    get ids() {
      return ids
    },
    get selectedItems() {
      return selectedItems
    },
		get pageSelectedCount() {
			return pageSelectedCount
		},
		get indeterminate() {
			return pageSelectedCount > 0 && pageSelectedCount < items.length
		},
		get noneSelected() {
			return pageSelectedCount === 0
		},
    get count() {
      return selected.size
    },
    get totalCount() {
      return totalCount
    },
    get empty() {
      return selected.size === 0
    },
    get populated() {
      return selected.size > 0
    },
    get pageSelected() {
      return pageSelected
    },
    get pageOnlySelected() {
      return pageOnlySelected
    },
    get allSelected() {
      return allSelected
    }
	}
}
